import {
  createRootRoute,
  createRoute,
  createRouter,
  useNavigate,
  useParams,
  type RouterHistory,
} from "@tanstack/react-router";

import { RootGate } from "@/app/RootGate";
import { CashScreen } from "@/features/cash/CashScreen";
import { PosScreen } from "@/features/pos/PosScreen";
import { CheckScreen } from "@/features/restaurant/CheckScreen";
import { RestaurantScreen } from "@/features/restaurant/RestaurantScreen";
import { CustomersScreen } from "@/features/customers/CustomersScreen";
import { InventoryScreen } from "@/features/inventory/InventoryScreen";
import { PurchasingScreen } from "@/features/purchasing/PurchasingScreen";
import { ReportsScreen } from "@/features/reports/ReportsScreen";
import { OrderDetailScreen } from "@/features/workshop/OrderDetailScreen";
import { WorkshopScreen } from "@/features/workshop/WorkshopScreen";
import { SuperadminScreen } from "@/features/saas/SuperadminScreen";
import { BusinessAdminScreen } from "@/features/organization/BusinessAdminScreen";
import { ProductsScreen } from "@/features/catalog/ProductsScreen";
import { BrandScreen } from "@/routes/brand-screen";
import { LandingScreen } from "@/features/marketing/LandingScreen";
import { ContactScreen } from "@/features/marketing/ContactScreen";
import { LoginScreen } from "@/features/auth/LoginScreen";

/**
 * Rutas tipadas.
 *
 * Con TanStack Router las rutas son objetos y la navegación se valida en
 * compilación: `to="/ventaa"` no compila. En un sistema que crecerá a decenas
 * de pantallas, eso evita una clase entera de errores tontos.
 *
 * La raíz es el `RootGate`: primero comprueba la sesión y, si la hay, monta el
 * `AppShell` (sidebar + barra superior) una sola vez. Las pantallas se
 * renderizan en su `<Outlet />`.
 */
const rootRoute = createRootRoute({
  component: RootGate,
});

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingScreen,
});

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contacto",
  component: ContactScreen,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: function LoginRoute() {
    const navigate = useNavigate();
    return (
      <LoginScreen
        onSuccess={() => {
          void navigate({ to: "/pos", replace: true });
        }}
      />
    );
  },
});

const posRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos",
  component: PosScreen,
});

const cashRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/caja",
  component: CashScreen,
});

const workshopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/taller",
  component: function WorkshopRoute() {
    const navigate = useNavigate();
    return <WorkshopScreen onOpenOrder={(id) => void navigate({ to: "/taller/$orderId", params: { orderId: id } })} />;
  },
});

const workshopOrderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/taller/$orderId",
  component: function WorkshopOrderRoute() {
    const { orderId } = useParams({ from: "/taller/$orderId" });
    const navigate = useNavigate();
    return <OrderDetailScreen orderId={orderId} onBack={() => void navigate({ to: "/taller" })} />;
  },
});

const restaurantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/restaurante",
  component: function RestaurantRoute() {
    const navigate = useNavigate();
    return (
      <RestaurantScreen
        onOpenCheck={(id) =>
          void navigate({
            to: "/restaurante/comanda/$checkId",
            params: { checkId: id },
          })
        }
      />
    );
  },
});

const restaurantCheckRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/restaurante/comanda/$checkId",
  component: function RestaurantCheckRoute() {
    const { checkId } = useParams({ from: "/restaurante/comanda/$checkId" });
    const navigate = useNavigate();
    return (
      <CheckScreen
        checkId={checkId}
        onBack={() => void navigate({ to: "/restaurante" })}
      />
    );
  },
});

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inventario",
  component: InventoryScreen,
});

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/productos",
  component: ProductsScreen,
});

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clientes",
  component: CustomersScreen,
});

const purchasingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/compras",
  component: PurchasingScreen,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reportes",
  component: ReportsScreen,
});

const superadminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/superadmin",
  component: SuperadminScreen,
});

const businessAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/administracion",
  component: BusinessAdminScreen,
});

const brandRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brand",
  component: BrandScreen,
});

export const routeTree = rootRoute.addChildren([
  landingRoute,
  contactRoute,
  loginRoute,
  posRoute,
  productsRoute,
  cashRoute,
  inventoryRoute,
  workshopRoute,
  workshopOrderRoute,
  restaurantRoute,
  restaurantCheckRoute,
  customersRoute,
  purchasingRoute,
  reportsRoute,
  superadminRoute,
  businessAdminRoute,
  brandRoute,
]);

/**
 * Fábrica del router.
 *
 * Permite inyectar un historial distinto: el navegador en producción, uno en
 * memoria en los tests. Gracias a esto, los tests montan **el router real** con
 * sus rutas reales, y no una imitación que se desactualiza sola.
 */
export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    ...(history ? { history } : {}),
    // Precarga la ruta al pasar el ratón por encima: en una caja, medio
    // segundo menos al cambiar de pantalla se nota al final del día.
    defaultPreload: "intent",
  });
}

export const router = createAppRouter();

/**
 * Registro de tipos: le dice a toda la app qué rutas existen.
 * Sin este bloque, `to="/brand"` sería un `string` cualquiera.
 */
declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
